import React, { useEffect, useMemo, useRef, useState } from 'react';
import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import { Box, Button, Grid, Stack } from '@mui/material';
import AddRefundRequest from './AddRefundRequest';
import GlobalDatagrid from '../../components/mui/DataGrid/GlobalDatagrid';
import { TableHead_Refund_fn } from '../../components/datagrid/refund/RefundRequestTableHead';
import { useSelector } from 'react-redux';
import { Icon } from '@iconify/react/dist/iconify.js';
import ViewRefundDetails from './ViewRefundDetails';
import GlobalRefundApprovalReject from './GlobalRefundApprovalReject';
import RequestStats from './RequestStats';
import { useNavigate } from 'react-router-dom';
import FromAndToDatePicker from '../../components/mui/FromAndToDatePicker';
import dayjs from 'dayjs';
import GlobalSelectFieldWithoutFormikNew from '../../components/mui/GlobalSelectFieldWithoutFormikNew';
import { useDispatch } from 'react-redux';
import { deleteRequest, getEnrolledRefundRequest, getNonEnrolledRefundRequest } from '../../redux/features/refundSlice';
import GlobalDialog from '../../components/mui/Dialogue/GlobalDialog';
import ReasonEdit from './verify_approve/components/ReasonEdit';
import TutorsTable from './verify_approve/components/TutorsTable';
import DetailsAction from './verify_approve/components/DetailsAction';
import { refundGlobalStatus, typeOfStudent } from '../../utils/constants/GlobalStaticData';
import GlobalDeleteDialogueContent from '../../components/GlobalDeleteDialogueContent';
import { openChat, setRefreashNeeded } from '../../redux/features/chatSlice';

const RefundRequest = () => {
  const [fromDate, setFromDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD')); //DEFAULT SET TO CURRENT MONTH STARTING DATE TO ENDING DATE
  const [toDate, setToDate] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));
  const [selectedStatus, setSelectedStatus] = useState({});
  const [selectedType, setSelectedType] = useState(typeOfStudent[0]);
  const { paginationData } = useSelector((state) => state.globalDataGrid);
  const { gridSize, pageNum, rowData, openAnchor, search } = paginationData;
  const { loading } = useSelector((state) => state.refund);

  const [openTutorsTable, setOpenTutorsTable] = useState(false);
  const [deleteDialogue, setDeleteDialogue] = useState(false);
  const [editReason, setEditReason] = useState('');
  const [openRejectionReason, setOpenRejectionReason] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [approvalModal, setApprovalModal] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const { open: chatOpenFromRedux, refreashNeeded } = useSelector((state) => state.chat);
  const prevOpenRef = useRef(chatOpenFromRedux);

  const {
    refundRequestTable: { data: tableRow, meta },
    submitionLoading,
  } = useSelector((state) => state.refund);

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const dispatchParams = useMemo(() => {
    return {
      gridSize: gridSize || 10,
      pageNum: pageNum || 1,
      search,
      fromDate,
      toDate,
      status: selectedStatus?.id,
      type: 1,
    };
  }, [pageNum, gridSize, search, fromDate, toDate, selectedStatus]);

  useEffect(() => {
    dispatch(selectedType?.id ? getNonEnrolledRefundRequest(dispatchParams) : getEnrolledRefundRequest(dispatchParams));
  }, [dispatch, selectedType, dispatchParams]);

  useEffect(() => {
    if (!chatOpen || !selectedRow) return;
    dispatch(
      openChat({
        chatRoomId: selectedRow?.chat_room_id || null,
        groupCreationUsersList: selectedRow?.related_users || [],
        groupName: `Refund - ${selectedRow?.student_name}-${selectedRow?.expense_id}`,
        rowDetails: selectedRow,
      })
    );
    setChatOpen(false);
  }, [chatOpen]);

  useEffect(() => {
    const justClosed = prevOpenRef.current === true && chatOpenFromRedux === false;
    if (justClosed && refreashNeeded) {
      dispatch(
        selectedType?.id ? getNonEnrolledRefundRequest(dispatchParams) : getEnrolledRefundRequest(dispatchParams)
      );
      dispatch(setRefreashNeeded(false));
    }
    prevOpenRef.current = chatOpenFromRedux;
  }, [chatOpenFromRedux, dispatchParams, selectedType, refreashNeeded]);

  const columns = useMemo(
    () =>
      TableHead_Refund_fn({
        setOpenTutorsTable,
        setOpenRejectionReason,
        selectedType: selectedType?.id,
        setOpenDetails,
        setChatOpen,
        setSelectedRow,
        setApprovalModal,
        setEditReason,
        Delete: true,
        setDeleteDialogue,
        approvalOrVerify: false,
      }),
    [selectedType]
  );

  const GLOBAL_DIALOGUE = [
    {
      open: openRejectionReason,
      setOpen: setOpenRejectionReason,
      title: 'Bank Account Details',
      content: (
        <ReasonEdit
          setOpen={setOpenRejectionReason}
          reason={editReason}
          rowData={selectedRow}
          non={Boolean(selectedType?.id)}
          dispatchParams={dispatchParams}
          submitDispatch={selectedType?.id ? getNonEnrolledRefundRequest : getEnrolledRefundRequest}
          open={openRejectionReason}
        />
      ),
    },
    {
      open: Boolean(openTutorsTable),
      setOpen: setOpenTutorsTable,
      title: 'Tutors',
      content: <TutorsTable ids={openTutorsTable} />,
    },
    {
      open: Boolean(openDetails),
      setOpen: setOpenDetails,
      title: 'Details',
      content: (
        <DetailsAction
          data={openDetails}
          dispatchParams={dispatchParams}
          submitDispatch={getNonEnrolledRefundRequest}
        />
      ),
    },
    {
      open: Boolean(deleteDialogue),
      setOpen: setDeleteDialogue,
      title: 'Delete Request',
      content: (
        <GlobalDeleteDialogueContent
          content={'Are you sure ?'}
          deleteDispatchParams={deleteDialogue}
          dispatchDeleteFn={deleteRequest}
          setOpen={setDeleteDialogue}
          successDispatchParams={dispatchParams}
          loading={submitionLoading}
          onSuccessDispatchFn={selectedType?.id ? getNonEnrolledRefundRequest : getEnrolledRefundRequest}
          cancel
        />
      ),
    },
  ];

  return (
    <GlobalWrapper
      title={'Refund Request'}
      actions={
        <Stack direction={'row'} spacing={2}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<Icon icon="streamline-freehand-color:cash-payment-coin-1" />}
            onClick={() => navigate('/my-workspace/new-refund-request')}
          >
            Add Refund Request
          </Button>
        </Stack>
      }
    >
      <Box sx={{ width: '100%' }}>
        <Box>
          <RequestStats loading={loading} requestStats={meta} />
        </Box>

        <Grid container spacing={2} my={3}>
          <Grid item xs={12} md={6} lg={4}>
            <FromAndToDatePicker
              disabled={false}
              fromDate={fromDate}
              toDate={toDate}
              setFromDate={setFromDate}
              setToDate={setToDate}
              fullWidth={true}
            />
          </Grid>

          {/*     <Grid item xs={12} md={6} lg={4}>
            <GlobalSelectFieldWithoutFormikNew
              keys={{ itemId: 'id', itemValue: 'label' }}
              data={refundGlobalStatus}
              label={'Refund Status'}
              fullWidth={true}
              selectedDetails={selectedStatus}
              setChange={setSelectedStatus}
            />
          </Grid> */}

          <Grid item xs={12} md={6} lg={4}>
            <GlobalSelectFieldWithoutFormikNew
              keys={{ itemId: 'id', itemValue: 'name' }}
              data={typeOfStudent}
              label={'Student Type'}
              fullWidth={true}
              selectedDetails={selectedType}
              setChange={setSelectedType}
            />
          </Grid>
        </Grid>

        <GlobalDatagrid
          tableHead={columns}
          rows={tableRow ?? []}
          meta={meta?.totalRecords || 0}
          loading={loading}
          rowUniqueId="sl_no"
          rowClick={false}
        />

        {GLOBAL_DIALOGUE.map(({ open, content, setOpen, title }, i) => (
          <GlobalDialog
            key={i}
            maxWidth={'100%'}
            content={content}
            open={open}
            setOpen={setOpen}
            closeButton={true}
            title={title}
          />
        ))}

        {approvalModal && (
          <GlobalRefundApprovalReject
            open={approvalModal}
            setOpen={setApprovalModal}
            rowData={selectedRow}
            dispatchParams={dispatchParams}
            loading={loading}
            apiDispatchAfterSuccess={getEnrolledRefundRequest}
          />
        )}
        {/* {chatOpen && (
          <ChatDrawer
            open={chatOpen}
            setOpen={setChatOpen}
            chatRoomId={selectedRow?.chat_room_id}
            groupCreationUsersList={selectedRow?.related_users}
            groupName={`Refund - ${selectedRow?.student_name}-${selectedRow?.expense_id}`}
            chatCloseFunction={() => {
              dispatch(
                selectedType?.id
                  ? getNonEnrolledRefundRequest(dispatchParams)
                  : getEnrolledRefundRequest(dispatchParams)
              );
            }}
            rowDetails={selectedRow}
          />
        )} */}
      </Box>
    </GlobalWrapper>
  );
};

export default RefundRequest;
